/** @jsxImportSource @emotion/react */
import React, {
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
  useEffect
} from 'react'
import {
  activeAccountNameSelector,
  activeAccountGroupSelector
} from '@signal/core/auth/selectors'
import { useContextsInReduxForm } from '@signal/core/contextFetch/hooks'
import {
  emailSelector,
  getReduxUserAndAccountCookers
} from '@signal/core/user/selectors'
import {
  ActiveAccountContext,
  UserDetailsContext
} from '@signal/core/user/Context'
import { UserDetailsType } from '@signal/core/user/types'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import { UserAccountType } from '@signal/core/user/types'
import { UseDynamicPathReplacementHookType } from '@signal/core/router/redirect/types'
import { AnimatePresence, motion } from 'framer-motion'
import UserPreviewNew from './UserPreviewNew'
import Accounts from './Accounts'
import {
  UserMenuWrapper,
  PopoverContainer,
  PopoverContent,
  DealerGroupSection,
  FullWidthDivider,
  ActionsContainer,
  ActionButton,
  PrivacyLinksContainer,
  PrivacyLink
} from './styles'
import { useCachedUserAccounts } from '@signal/core/user/hooks'
import { Typography } from '@material-ui/core'
import { ArrowForward, Brightness6 } from '@material-ui/icons'
import DeveloperOptions from './DeveloperOptions'
import { useDarkModeContext } from '@signal/theme/Context/dark-mode'
import renderIf from '@signal/utils/renderIf'
import { useLicenseInformationPopup } from '@purecarslabs/signal-containers/LicenseInformation/hooks'

export type UserMenuProps = {
  accountNameOverride?: string
  defaultOpen?: boolean
  onAccountChange: ({ accountId }: { accountId: string }) => void
  onLogout: () => void
  useDynamicPathReplacementHook?: UseDynamicPathReplacementHookType
  showFontSlider?: boolean
  UserMenuNavLinks?: React.ComponentType<Record<string, unknown>>
  isSkeleton?: boolean
  [key: string]: unknown // For additional props
}

// Framer Motion variants for the popover slide down animation
const popoverVariants = {
  hidden: {
    opacity: 0.5,
    y: -500,
    transformOrigin: 'top'
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    y: -500,
    transition: {
      duration: 0.25,
      ease: [0.4, 0.0, 1, 1]
    }
  }
}

// Animation variants for individual sections
export const sectionVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: -20
  },
  visible: (custom: number = 1) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
      delay: 0.2 * custom
    }
  })
}

const UserMenu: React.FC<UserMenuProps> = props => {
  const {
    accountNameOverride,
    defaultOpen = false,
    onAccountChange,
    onLogout,
    useDynamicPathReplacementHook,
    isSkeleton
  } = props

  const [isOpen, setIsOpen] = useState(defaultOpen)
  const menuRef = useRef<HTMLDivElement>(null)

  const data = useCachedUserAccounts()

  const { changeActiveAccount, recentAccounts } =
    useContext(ActiveAccountContext)
  const userDetails = useContext(UserDetailsContext)
  const { setIsDarkMode, isDarkMode } = useDarkModeContext()
  const { handleShowLicenseInformation } = useLicenseInformationPopup()

  // @ts-ignore
  const selectorState = useContextsInReduxForm(getReduxUserAndAccountCookers())
  const title = accountNameOverride || activeAccountNameSelector(selectorState)
  const email = emailSelector(selectorState)
  const activeAccountGroup = activeAccountGroupSelector(selectorState)

  // Check for impersonation and developer state
  const { isDeveloper, isImpersonating } = useMemo(
    () => ({
      isDeveloper:
        (userDetails?.response as UserDetailsType | undefined)
          ?.isDeveloperToken || false,
      isImpersonating:
        (userDetails?.response as UserDetailsType | undefined)
          ?.isImpersonating || false
    }),
    [userDetails]
  )

  const handleAccountChange = useCallback(
    (account: UserAccountType) => {
      if (changeActiveAccount) {
        changeActiveAccount(account)
      }
      onAccountChange({ accountId: account.accountId })
    },
    [onAccountChange, changeActiveAccount]
  )

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const closeMenu = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleDealerGroupClick = useCallback(
    (account: UserAccountType) => () => {
      handleAccountChange(account)
      closeMenu()
    },
    [handleAccountChange, closeMenu]
  )

  const handleAccountsChange = useCallback(
    ({ accountId }: { accountId: string }) => {
      // Find the full account object from the available data
      const account =
        data?.find(acc => acc.accountId === accountId) ||
        recentAccounts?.find(acc => acc.accountId === accountId)
      if (account) {
        handleAccountChange(account as UserAccountType)
      }
    },
    [handleAccountChange, data, recentAccounts]
  )

  const handleLogout = useCallback(() => {
    onLogout()
    closeMenu()
  }, [onLogout, closeMenu])

  const handleDarkModeToggle = useCallback(() => {
    setIsDarkMode(!isDarkMode)
  }, [setIsDarkMode, isDarkMode])

  const handlePrivacyLinkClick = useCallback(() => {
    closeMenu()
  }, [closeMenu])

  const handleLicenseInfoClick = useCallback(() => {
    closeMenu()
    handleShowLicenseInformation()
  }, [closeMenu, handleShowLicenseInformation])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen, closeMenu])

  return (
    <UserMenuWrapper ref={menuRef}>
      <UserPreviewNew
        isSkeleton={isSkeleton}
        title={title}
        email={email}
        isImpersonating={isImpersonating}
        onClick={toggleMenu}
      />

      <AnimatePresence>
        {isOpen && (
          <PopoverContainer
            variants={popoverVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            $isDarkMode={isDarkMode}
          >
            <PopoverContent>
              {activeAccountGroup && (
                <motion.div
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  custom={0.6}
                >
                  <DealerGroupSection
                    onClick={handleDealerGroupClick(activeAccountGroup)}
                    $isDarkMode={isDarkMode}
                  >
                    <div>
                      <Typography
                        style={{
                          fontSize: '13px',
                          lineHeight: '1'
                        }}
                      >
                        Dealer Group
                      </Typography>
                      <Typography
                        style={{
                          fontSize: '14px',
                          fontWeight: 600
                        }}
                      >
                        {activeAccountGroup.accountName}
                      </Typography>
                    </div>
                    <ArrowForward />
                  </DealerGroupSection>
                </motion.div>
              )}

              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                custom={1.4}
              >
                <Accounts
                  recentAccounts={recentAccounts}
                  allAccounts={data}
                  onAccountChange={handleAccountsChange}
                  onCloseMenu={closeMenu}
                  useDynamicPathReplacementHook={useDynamicPathReplacementHook}
                  isDarkMode={isDarkMode}
                />
              </motion.div>

              {renderIf(isDeveloper || isImpersonating, () => (
                <motion.div
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  custom={1.9}
                >
                  <DeveloperOptions
                    isImpersonating={isImpersonating}
                    currentUserEmail={email}
                  />
                </motion.div>
              ))}

              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                custom={2}
              >
                <FullWidthDivider $isDarkMode={isDarkMode} />
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                custom={2}
              >
                <ActionsContainer>
                  <ActionButton onClick={handleLogout} $fullHeight={true}>
                    {'Sign Out'}
                  </ActionButton>
                  <ActionButton
                    onClick={handleDarkModeToggle}
                    $fullHeight={true}
                    $color={isDarkMode ? 'SHINE_BLUE' : 'BASE'}
                  >
                    {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                    <Brightness6 />
                  </ActionButton>
                </ActionsContainer>
              </motion.div>

              {/* Privacy Links */}
              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                custom={2.5}
              >
                <PrivacyLinksContainer>
                  <motion.div variants={sectionVariants}>
                    <Typography variant="caption">
                      <PrivacyLink
                        href="https://www.purecars.com/privacy-policy/"
                        target="_blank"
                        onClick={handlePrivacyLinkClick}
                      >
                        Privacy Policy
                      </PrivacyLink>
                    </Typography>
                  </motion.div>
                  <motion.div variants={sectionVariants}>
                    <Typography variant="caption">
                      <PrivacyLink
                        href="https://www.purecars.com/terms-service-agreement/"
                        target="_blank"
                        onClick={handlePrivacyLinkClick}
                      >
                        Terms & Conditions
                      </PrivacyLink>
                    </Typography>
                  </motion.div>
                  <motion.div variants={sectionVariants}>
                    <Typography variant="caption">
                      <PrivacyLink
                        onClick={handleLicenseInfoClick}
                        style={{ cursor: 'pointer' }}
                      >
                        License Info
                      </PrivacyLink>
                    </Typography>
                  </motion.div>
                </PrivacyLinksContainer>
              </motion.div>
            </PopoverContent>
          </PopoverContainer>
        )}
      </AnimatePresence>
    </UserMenuWrapper>
  )
}

export default memo(withCommonSnapshotsOnly(UserMenu))
